import { supabase } from '@/lib/supabase';
import { appStorage } from '@/lib/appStorage';
import { clearImageCache } from '@/services/imagePreloader';
import { cacheService } from '@/services/cacheService';

// Lista centralizada de chaves do localStorage que precisam ser limpas
const USER_RELATED_KEYS = [
  'user',
  'user_profile',
  'user_session',
  'journey_progress',
  'player_isShuffled',
  'player_originalQueue',
  'daily_verses_cache',
  'songs_cache',
  'favorites_cache',
  'playlist_cache'
] as const;

// Padrões para identificar chaves específicas do usuário
const USER_SPECIFIC_PATTERNS = [
  'user_',
  'journey_',
  'cache_'
] as const;

/**
 * Limpa todos os dados do usuário e cache de forma segura e otimizada.
 * @param skipSignOut Se true, não faz logout do Supabase
 * @returns Promise<void>
 */
export async function clearAllUserData(skipSignOut: boolean = false): Promise<void> {
  try {
    // 1. Limpa sessão do Supabase se existir e skipSignOut for false
    if (!skipSignOut) {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        await supabase.auth.signOut();
        // Pequeno delay para garantir que a sessão foi limpa
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    // 2. Limpa chaves conhecidas
    const removalPromises = USER_RELATED_KEYS.map(key => appStorage.removeItem(key));
    await Promise.all(removalPromises);

    // 3. Limpa chaves que seguem padrões específicos
    try {
      const allKeys = Object.keys(localStorage);
      const userSpecificKeys = allKeys.filter(key =>
        USER_SPECIFIC_PATTERNS.some(pattern => key.includes(pattern))
      );

      userSpecificKeys.forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.warn('[Cleanup] Erro ao limpar chaves específicas:', error);
    }

    // 4. Limpa cache de imagens
    clearImageCache();

    // 5. Limpa Service Worker cache se disponível
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      } catch (error) {
        console.warn('[Cleanup] Erro ao limpar cache do Service Worker:', error);
      }
    }

    // 6. Limpa sessionStorage
    try {
      sessionStorage.clear();
    } catch (error) {
      console.warn('[Cleanup] Erro ao limpar sessionStorage:', error);
    }

  } catch (error) {
    console.error('[Cleanup] Erro durante limpeza:', error);
    // FALLBACK: Limpeza mínima em caso de erro
    try {
      localStorage.removeItem('user');
      sessionStorage.clear();
    } catch (fallbackError) {
      console.error('[Cleanup] Erro durante fallback:', fallbackError);
    }
  }
}

/**
 * Limpa apenas dados de cache, mantendo dados do usuário.
 * Usado para otimização de performance.
 */
export async function clearCacheData(): Promise<void> {
  try {
    // 1. Limpa cache unificado (limpa automaticamente chaves antigas do localStorage)
    await cacheService.cleanExpired();

    // 2. Limpa chaves de cache legadas (mantidas para compatibilidade)
    const legacyCacheKeys = [
      'daily_verses_cache',
      'songs_cache',
      'playlist_cache'
    ];

    legacyCacheKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`[Cache] Erro ao limpar ${key}:`, error);
      }
    });

    // 3. Limpa cache antigo (mais de 7 dias) - padrões legados
    const now = Date.now();
    const oneWeekAgo = now - (7 * 24 * 60 * 60 * 1000);
    
    Object.keys(localStorage).forEach(key => {
      if (key.includes('_cache_') || key.includes('preload_')) {
        try {
          const item = JSON.parse(localStorage.getItem(key) || '{}');
          if (item.timestamp && item.timestamp < oneWeekAgo) {
            localStorage.removeItem(key);
          }
        } catch {
          // Se não conseguir parsear, remove
          localStorage.removeItem(key);
        }
      }
    });

    // 4. Limpa cache de imagens
    clearImageCache();

  } catch (error) {
    console.error('[Cache] Erro ao limpar cache:', error);
  }
} 